import moment, { Moment } from "moment";
import { useFormContext } from "react-hook-form";

/**
 Filters orders based on the pickup and delivery date range and time criteria from form values
*/
export function useFilteredOrders(orders: any[]) {
  const methods = useFormContext();
  const { watch } = methods;

  // Watch the form values
  const {
    pickup_cargo_ready,
    pickup_ready_date,
    pickup_ready_time,
    pickup_before_date,
    pickup_before_time,

    delivery_cargo_ready,
    stop1_after_date,
    stop1_after_time,
    stop1_before_date,
    stop1_before_time,
  } = watch("schedule") || {};

  // Convert the pickup_ready_date and pickup_before_date to moment objects and convert to UTC
  const readyDatePickup =
    pickup_ready_date && pickup_ready_time ? moment(`${pickup_ready_date} ${pickup_ready_time}`).utc() : null;
  const beforeDatePickup =
    pickup_before_date && pickup_before_time ? moment(`${pickup_before_date} ${pickup_before_time}`).utc() : null;

  // Convert the stop1_after_date and stop1_before_date to moment objects and convert to UTC
  const afterDateDelivery =
    stop1_after_date && stop1_after_time ? moment(`${stop1_after_date} ${stop1_after_time}`).utc() : null;
  const beforeDateDelivery =
    stop1_before_date && stop1_before_time ? moment(`${stop1_before_date} ${stop1_before_time}`).utc() : null;

  // Filter orders for pickup date range
  const filteredByPickup = orders.filter((order) => {
    const pickup_date = order.data.schedule?.pickup_date;
    const pickup_time = order.data.schedule?.pickup_time;
    const pickupDateTime = pickup_date && pickup_time ? moment(`${pickup_date} ${pickup_time}`).utc() : null;

    if (pickup_cargo_ready === "between" && readyDatePickup && beforeDatePickup) {
      return pickupDateTime && pickupDateTime.isValid()
        ? !pickupDateTime.isBetween(readyDatePickup, beforeDatePickup, null, "[]")
        : false;
    }
    if (pickup_cargo_ready === "after" && readyDatePickup) {
      return pickupDateTime && pickupDateTime.isValid() ? pickupDateTime.isAfter(readyDatePickup) : false;
    }

    return true;
  });

  // Filter orders for delivery date range
  const filteredByDelivery = orders.filter((order) => {
    const stop1_date = order.data.schedule?.stop1_date;
    const stop1_time = order.data.schedule?.stop1_time;
    const deliveryDateTime = stop1_date && stop1_time ? moment(`${stop1_date} ${stop1_time}`).utc() : null;

    if (delivery_cargo_ready === "between" && afterDateDelivery && beforeDateDelivery) {
      return deliveryDateTime && deliveryDateTime.isValid()
        ? !deliveryDateTime.isBetween(afterDateDelivery, beforeDateDelivery, null, "[]")
        : true;
    }
    if (delivery_cargo_ready === "before" && afterDateDelivery) {
      return deliveryDateTime && deliveryDateTime.isValid() ? deliveryDateTime.isBefore(afterDateDelivery) : true;
    }

    return true;
  });

  const filteredPickupOrders = filteredByPickup.map((order) => order.data.organization.quick_request_reference);

  const filteredDeliveryOrders = filteredByDelivery.map((order) => order.data.organization.quick_request_reference);

  return { filteredPickupOrders, filteredDeliveryOrders };
}

/**
  Calculates the date and time range for pickup and stop1 events from order data:
*/
export function getOrderDateAndTimeRange(data: any[]) {
  let minPickupDateTime: Moment | null = null;
  let maxPickupDateTime: Moment | null = null;

  let minStop1DateTime: Moment | null = null;
  let maxStop1DateTime: Moment | null = null;

  data?.forEach((order) => {
    const { pickup_datetime, pickup_end_datetime, stop1_datetime, stop1_end_datetime } = order?.data?.schedule || {};

    // Parse datetime strings to moment objects, with checks for optional end times
    const pickupDateTime = pickup_datetime ? moment(pickup_datetime, "MMMM DD YYYY - HH:mm [UTC]ZZ") : null;
    const pickupEndDateTime = pickup_end_datetime ? moment(pickup_end_datetime, "MMMM DD YYYY - HH:mm [UTC]ZZ") : null;
    const stop1DateTime = stop1_datetime ? moment(stop1_datetime, "MMMM DD YYYY - HH:mm [UTC]ZZ") : null;
    const stop1EndDateTime = stop1_end_datetime ? moment(stop1_end_datetime, "MMMM DD YYYY - HH:mm [UTC]ZZ") : null;

    // Set min for pickup date range
    if (pickupDateTime && pickupDateTime.isValid() && (!minPickupDateTime || pickupDateTime.isBefore(minPickupDateTime))) {
      minPickupDateTime = pickupDateTime;
    }

    // Set max for pickup date range
    if (pickupDateTime && pickupDateTime.isValid() && (!maxPickupDateTime || pickupDateTime.isAfter(maxPickupDateTime))) {
      maxPickupDateTime = pickupDateTime;
    }
    if (pickupEndDateTime && pickupEndDateTime.isValid() && pickupEndDateTime.isAfter(maxPickupDateTime)) {
      maxPickupDateTime = pickupEndDateTime;
    }

    // Set min for stop1 date range
    if (stop1DateTime && stop1DateTime.isValid() && (!minStop1DateTime || stop1DateTime.isBefore(minStop1DateTime))) {
      minStop1DateTime = stop1DateTime;
    }
    // Set max for stop1 date range
    if (
      !maxStop1DateTime ||
      (stop1EndDateTime && stop1EndDateTime.isValid() && stop1EndDateTime.isAfter(maxStop1DateTime)) ||
      (stop1EndDateTime && stop1EndDateTime.isValid() && stop1EndDateTime.isAfter(maxStop1DateTime))
    ) {
      maxStop1DateTime = stop1EndDateTime || stop1DateTime;
    }
  });

  // Format the date ranges for pickup and stop1 if min and max dates are set
  const pickupDateRange =
    minPickupDateTime && maxPickupDateTime
      ? `Between ${moment(minPickupDateTime).format("DD MMM YYYY")} to ${moment(maxPickupDateTime).format("DD MMM YYYY")}`
      : "";

  const stop1DateRange =
    minStop1DateTime && maxStop1DateTime
      ? `Between ${moment(minStop1DateTime).format("DD MMM YYYY")} to ${moment(maxStop1DateTime).format("DD MMM YYYY")}`
      : "";

  return {
    pickupDateRange,
    stop1DateRange,
  };
}
